/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable no-param-reassign */

import {
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import AutocompleteSelect from 'components/Autocomplete';
import Button from 'components/Button';
import Loading from 'components/Loading';
import TextEditor from 'components/TextEditor';
import { Formik } from 'formik';
import i18n from 'i18n';
import {
  editEnvironmentBehaviorRequest,
  getAllServersRequest,
  getEnvironmentBehaviorRequest,
  getTimezonesRequest,
} from 'modules/environments/actions';
import {
  EnvironmentBehaviorFieldsProps,
  EnvironmentFields,
  ListServerFieldsDataProps,
  TimezonesFields,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { applyI18n } from 'utils/helpers';
import { GetAllServersRequestProps } from 'modules/environments/requestInterfaces';
import screenFields from './formFields';
import FormPeriods from './FormPeriods';
import styles from './styles';
import TitleBehavior from './TitleBehavior';

const useStyles = makeStyles(styles);

interface Props {
  environmentBehaviorWeek: EnvironmentBehaviorFieldsProps;
  environmentBehaviorSaturday: EnvironmentBehaviorFieldsProps;
  environmentBehaviorSunday: EnvironmentBehaviorFieldsProps;
  environmentComments: string;
  environmentId: string;
  environmentSelected: EnvironmentFields | null;
  environmentTimeZone: string;
  isLoading: boolean;
  modeName: string;
  timezonesList: TimezonesFields;
  supportServers: ListServerFieldsDataProps;
  getEnvironmentBehaviorRequest: (props: { environmentId: string }) => void;
  editEnvironmentBehaviorRequest: (props: {
    environmentId: string;
    values: any;
  }) => void;
  getTimezonesRequest: () => void;
  getAllServersRequest: (props: GetAllServersRequestProps) => void;
}

const removeGMTFromTimezone = (timezone?: string) =>
  timezone?.replace(/ \(GMT[^)]+\)$/, '');

const FormBehavior = ({
  environmentBehaviorWeek,
  environmentBehaviorSaturday,
  environmentBehaviorSunday,
  environmentComments,
  environmentId,
  environmentSelected,
  environmentTimeZone,
  isLoading,
  modeName,
  timezonesList,
  supportServers,
  getEnvironmentBehaviorRequest,
  editEnvironmentBehaviorRequest,
  getTimezonesRequest,
  getAllServersRequest,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [hasError, setHasError] = React.useState(false);
  const [behaviorMode, setBehaviorMode] = React.useState({
    mode: modeName,
    timeZone: environmentTimeZone,
  });
  const [
    initialBehaviorEnvironment,
    setInitialBehaviorEnvironment,
  ] = React.useState({
    mode: '',
    timeZone: '',
  });
  const [comments, setComments] = React.useState<string>();

  const date = new Date();
  const timezoneSelected = {
    timeZone:
      removeGMTFromTimezone(behaviorMode?.timeZone) || 'America/Sao_Paulo',
    hour: '2-digit' as const,
    minute: '2-digit' as const,
  };
  const timezoneCurrentTime = date.toLocaleString(
    'pt-BR',
    timezoneSelected || environmentTimeZone
  );

  const getSupportServers = (getFromProvider = false) => {
    getAllServersRequest({
      id,
      query: { offset: 0, limit: 10, search: '' },
      force_refresh: getFromProvider,
      type: 'support',
    });
  };

  React.useEffect(() => {
    if (environmentSelected?.behavior.type === 'micro') {
      getSupportServers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentSelected, id]);

  const [behaviorWeek, setBehaviorWeek] = React.useState(
    environmentBehaviorWeek
  );
  const [behaviorSaturday, setBehaviorSaturday] = React.useState(
    environmentBehaviorSaturday
  );
  const [behaviorSunday, setBehaviorSunday] = React.useState(
    environmentBehaviorSunday
  );
  const hasApplicationServer = !!supportServers?.results?.some(
    server => server.is_application_server
  );

  React.useEffect(() => {
    setBehaviorWeek(environmentBehaviorWeek);
    setBehaviorSaturday(environmentBehaviorSaturday);
    setBehaviorSunday(environmentBehaviorSunday);
  }, [
    environmentBehaviorWeek,
    environmentBehaviorSaturday,
    environmentBehaviorSunday,
  ]);

  React.useEffect(() => {
    getEnvironmentBehaviorRequest({ environmentId });
  }, [environmentId, getEnvironmentBehaviorRequest]);

  React.useEffect(() => {
    setInitialBehaviorEnvironment({
      mode: modeName,
      timeZone: environmentTimeZone,
    });
  }, [environmentTimeZone, modeName]);

  const onSubmit = () => {
    editEnvironmentBehaviorRequest({
      environmentId,
      values: {
        week: behaviorWeek,
        saturday: behaviorSaturday,
        sunday: behaviorSunday,
        mode: {
          behavior: {
            type: behaviorMode.mode,
            timezone: behaviorMode.timeZone,
            comments,
          },
        },
      },
    });
  };

  const handleGetBehaviorMode = (key: string, value: string) => {
    if (key === 'mode') {
      setBehaviorMode({
        mode: value,
        timeZone: behaviorMode.timeZone,
      });
    } else {
      setBehaviorMode({
        mode: behaviorMode.mode,
        timeZone: value,
      });
    }
  };
  const getTimezones = () => {
    getTimezonesRequest();
  };

  React.useEffect(() => {
    getTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timezoneInitialValues = (timezone: string) => {
    const timezoneSelected = timezonesList.timezones.find(
      item => removeGMTFromTimezone(item) === timezone
    );
    return {
      value: removeGMTFromTimezone(timezoneSelected),
      label: timezoneSelected,
    };
  };

  return isLoading ? (
    <Loading />
  ) : (
    <Grid container xs={12}>
      <Grid xs={12}>
        <TitleBehavior title={i18n.t('ENVIRONMENTS_PAGE.BEHAVIOR_TITLE_KEY')} />
        <Grid item spacing={2} container xs={12} alignItems="baseline">
          <Formik
            initialValues={initialBehaviorEnvironment}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ setFieldValue, values, errors }) => (
              <>
                <Grid item xs={5}>
                  <GenericForm
                    overrideClasses={{
                      selectLabel: { fontSize: 12, fontWeight: 500 },
                    }}
                    fields={values}
                    screenFields={applyI18n(
                      screenFields.behaviorMode.slice(0, 1).map(item => {
                        return {
                          ...item,
                          disabled: hasApplicationServer,
                          options:
                            environmentSelected?.provider?.type === 'oci'
                              ? item.options?.filter(
                                  option => option.value !== 'spot_period'
                                )
                              : item.options,
                        };
                      }),

                      i18n.language
                    )}
                    changeField={({ key, value }) =>
                      handleGetBehaviorMode(key, value)
                    }
                    setFieldValue={setFieldValue}
                    errors={errors}
                  />
                  {hasApplicationServer && (
                    <Typography className={classes.helperText}>
                      {i18n.t(
                        'CANNOT_CHANGE_MODE_FROM_MICRO_WITH_APPLICATION_SERVER_ATTACHED'
                      )}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={7} md={5}>
                  <AutocompleteSelect
                    options={timezonesList?.timezones?.map(item => ({
                      value: removeGMTFromTimezone(item) || '',
                      label: item,
                    }))}
                    stateKey="timeZone"
                    label={i18n.t('TIME_ZONE_KEY')}
                    gridXs={12}
                    initialValuesAutoComplete={timezoneInitialValues(
                      values?.timeZone
                    )}
                    setFieldValue={(key, value) => {
                      setFieldValue(key, value);
                      handleGetBehaviorMode(key, value);
                    }}
                    errors={errors}
                    disableClearable
                  />
                </Grid>
                <Grid item xs={6} md={2} className={classes.currentTime}>
                  <TextField
                    id="current-timezone-time"
                    label={removeGMTFromTimezone(behaviorMode?.timeZone)}
                    value={timezoneCurrentTime}
                    variant="outlined"
                    type="datetime"
                    helperText={i18n.t('CURRENT_TIMEZONE_TIME_KEY')}
                    InputProps={{ readOnly: true }}
                    disabled={true}
                  />
                </Grid>
              </>
            )}
          </Formik>
        </Grid>

        {behaviorMode.mode !== 'manual' && (
          <FormPeriods
            mode={behaviorMode?.mode}
            setBehaviorSaturday={setBehaviorSaturday}
            setBehaviorSunday={setBehaviorSunday}
            setBehaviorWeek={setBehaviorWeek}
            setHasError={setHasError}
          />
        )}

        <Grid item xs={12}>
          <TitleBehavior
            title={i18n.t('ADD_COMMENT_TITLE_KEY')}
            subtitle={i18n.t('ADD_COMMENT_SUBTITLE_KEY')}
          />

          <Grid xs={6}>
            <TextEditor
              initialValue={environmentComments}
              getValue={value => setComments(value)}
              title={i18n.t('ADD_COMMENT_KEY')}
            />
          </Grid>
        </Grid>

        <Grid container justify="flex-end" className={classes.buttons}>
          <Button
            id="submit-button"
            type="submit"
            submitButton
            onClick={onSubmit}
            disabled={isLoading || hasError}
          >
            {isLoading ? <CircularProgress size={15} /> : i18n.t('SAVE_KEY')}
          </Button>
          <Button
            id="cancel-button"
            variant="outlined"
            onClick={() => history.push('/environments')}
          >
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: {
    environmentSelected,
    environmentBehaviorWeek,
    environmentBehaviorSaturday,
    environmentBehaviorSunday,
    timezonesList,
    supportServers,
  },
}: ApplicationState) => ({
  environmentSelected,
  environmentBehaviorWeek,
  environmentBehaviorSaturday,
  environmentBehaviorSunday,
  timezonesList,
  supportServers,
});

export default connect(mapStateToProps, {
  getEnvironmentBehaviorRequest,
  editEnvironmentBehaviorRequest,
  getTimezonesRequest,
  getAllServersRequest,
})(FormBehavior);
